import { UseFormReturn } from 'react-hook-form';
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormFieldProps, FormValues } from '@/types/supportTypes';
import { BRAND_CONFIG } from '@/types/brands';

interface SupportFormFieldProps {
  readonly field: FormFieldProps;
  readonly form: UseFormReturn<FormValues>;
  readonly filteredCategories: Record<string, readonly string[]>;
  readonly attachedFiles?: File[];
  readonly onAttachFiles?: (files: File[]) => void;
}

export function SupportFormField({ field, form, filteredCategories }: SupportFormFieldProps) {
  if (field.type === 'radio' && field.id === 'newsletter') {
    return (
      <div className="space-y-4">
        <Select
          value={field.value as string}
          onValueChange={(value: string) => {
            field.onChange(value);
            form.setValue('main', '');
            form.setValue('issue', '');
          }}
        >
          <SelectTrigger className="w-full bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#ffce04] focus:ring-2 focus:ring-[#ffce04]/20 transition-all h-14 text-base shadow-sm hover:border-gray-300">
            <SelectValue placeholder="Para qual marca é o seu chamado?" />
          </SelectTrigger>
          <SelectContent className="bg-white border border-gray-200 rounded-xl shadow-lg">
            {Object.entries(BRAND_CONFIG).map(([key, brand]) => (
              <SelectItem
                key={key}
                value={key}
                className="text-base py-3 px-4 hover:bg-[#ffce04]/5 cursor-pointer focus:bg-[#ffce04]/10 data-[highlighted]:bg-[#ffce04]/10"
              >
                {brand.newName !== brand.oldName ? `${brand.newName} (${brand.oldName})` : brand.newName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === 'radio' && field.id === 'main') {
    return (
      <div className="space-y-4">
        <Select
          value={field.value as string}
          onValueChange={(value: string) => {
            field.onChange(value);
            form.setValue('issue', '');
          }}
        >
          <SelectTrigger className="w-full bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#ffce04] focus:ring-2 focus:ring-[#ffce04]/20 transition-all h-14 text-base shadow-sm hover:border-gray-300">
            <SelectValue placeholder="Qual categoria você precisa de ajuda?" />
          </SelectTrigger>
          <SelectContent className="bg-white border border-gray-200 rounded-xl shadow-lg">
            {Object.keys(filteredCategories).map((category) => (
              <SelectItem
                key={category}
                value={category}
                className="text-base py-3 px-4 hover:bg-[#ffce04]/5 cursor-pointer focus:bg-[#ffce04]/10 data-[highlighted]:bg-[#ffce04]/10"
              >
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === 'radio' && field.id === 'issue') {
    const mainCategory = form.getValues('main');
    const issues = filteredCategories[mainCategory as keyof typeof filteredCategories] || [];

    if (['Feedback e Erros', 'Outros', 'Quiz'].includes(mainCategory)) {
      window.location.href = 'https://tally.so/r/w2o7GL';
      return null;
    }

    return (
      <div className="space-y-4">
        <Select
          value={field.value as string}
          onValueChange={(value: string) => field.onChange(value)}
          disabled={!mainCategory}
        >
          <SelectTrigger className="w-full bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#ffce04] focus:ring-2 focus:ring-[#ffce04]/20 transition-all h-14 text-base shadow-sm hover:border-gray-300">
            <SelectValue placeholder={mainCategory ? "Selecione o problema que está enfrentando" : "Selecione primeiro uma categoria"} />
          </SelectTrigger>
          <SelectContent className="bg-white border border-gray-200 rounded-xl shadow-lg">
            {issues.map((issue) => (
              <SelectItem
                key={issue}
                value={issue}
                className="text-base py-3 px-4 hover:bg-[#ffce04]/5 cursor-pointer focus:bg-[#ffce04]/10 data-[highlighted]:bg-[#ffce04]/10"
              >
                {issue}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return null;
  }

  if (field.type === 'textarea') {
    return (
      <div className="space-y-2">
        <textarea
          placeholder={field.placeholder}
          value={field.value as string}
          onChange={(e) => field.onChange(e.target.value)}
          className="w-full min-h-[150px] p-4 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 resize-none focus:border-[#ffce04] focus:ring-2 focus:ring-[#ffce04]/20 transition-all shadow-sm hover:border-gray-300"
        />
        <div className="text-sm text-gray-500 text-right">
          {(field.value as string).length}/10 caracteres mínimos
        </div>
      </div>
    );
  }

  return (
    <Input
      type={field.type || 'text'}
      placeholder={field.placeholder}
      value={field.value as string}
      onChange={(e) => field.onChange(e.target.value)}
      className="bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#ffce04] focus:ring-2 focus:ring-[#ffce04]/20 transition-all shadow-sm hover:border-gray-300 h-14"
    />
  );
}
